import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Link as LinkIcon,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckSquare,
  Square,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';
import { getUsers, getSessions } from '../../services/api';
import * as annotationApi from '../../services/annotationApi';
import { UserEmail } from '../../components/common/UserEmail';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserRecord {
  userId: string;
  name?: string;
  email?: string;
  role?: string;
}

interface SessionRecord {
  sessionId: string;
  enumeratorId?: string;
  status?: string;
  createdAt?: string;
  location?: { state?: string; district?: string };
}

interface GenericAssignmentRow {
  assignmentId: string;
  assigneeId: string;
  assigneeName?: string;
  createdAt: string;
}

// ─── Toast ─────────────────────────────────────────────────────────────────────

interface ToastMsg {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastCounter = 0;

function useToasts() {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const add = (type: 'success' | 'error', message: string) => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  };
  return { toasts, add };
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({
  open,
  message,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <p className="text-sm text-gray-700 mb-5">{message}</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 text-sm rounded-lg font-medium bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-3 py-2 text-sm rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Subject List (generic) ────────────────────────────────────────────────────

function SubjectList({
  label,
  subjects,
  selectedId,
  onSelect,
}: {
  label: string;
  subjects: UserRecord[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');

  // Substring match against email first (the user's stated lookup key) and
  // name as a courtesy fallback. Case-insensitive, trimmed.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subjects;
    return subjects.filter((u) => {
      const email = (u.email ?? '').toLowerCase();
      const name = (u.name ?? '').toLowerCase();
      return email.includes(q) || name.includes(q);
    });
  }, [subjects, query]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-700">{label}</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          {query.trim()
            ? `${filtered.length} of ${subjects.length} approved`
            : `${subjects.length} approved`}
        </p>
      </div>
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email…"
            className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-gray-100 max-h-[calc(100vh-340px)] overflow-y-auto">
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">
            {query.trim() ? 'No matches.' : 'No approved users found.'}
          </div>
        )}
        {filtered.map((u) => (
          <button
            key={u.userId}
            onClick={() => onSelect(u.userId)}
            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
              selectedId === u.userId
                ? 'bg-violet-50 border-r-2 border-r-violet-600'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {u.name ?? u.userId}
              </p>
              {u.email && (
                <div className="mt-0.5">
                  <UserEmail email={u.email} role={u.role} />
                </div>
              )}
            </div>
            <ChevronRight
              className={`w-4 h-4 flex-shrink-0 ${
                selectedId === u.userId ? 'text-violet-600' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Generic Assignment Panel ──────────────────────────────────────────────────

function AssignmentPanel({
  subjectName,
  subjectRoleLabel,
  assigneeRoleLabel,
  assigneeCandidates,
  assignments,
  isLoading,
  onAdd,
  onRemove,
  removeConfirmMessage,
}: {
  subjectName: string;
  subjectRoleLabel: string;
  assigneeRoleLabel: string;
  assigneeCandidates: UserRecord[];
  assignments: GenericAssignmentRow[];
  isLoading: boolean;
  onAdd: (assigneeId: string) => Promise<unknown>;
  onRemove: (assignmentId: string) => Promise<unknown>;
  removeConfirmMessage: string;
}) {
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const alreadyAssigned = new Set(assignments.map((a) => a.assigneeId));
  const available = assigneeCandidates.filter((u) => !alreadyAssigned.has(u.userId));

  const handleAdd = async () => {
    if (!selectedAssigneeId) return;
    setIsAdding(true);
    await onAdd(selectedAssigneeId);
    setSelectedAssigneeId('');
    setIsAdding(false);
  };

  const handleRemoveConfirm = async () => {
    if (!confirmRemoveId) return;
    await onRemove(confirmRemoveId);
    setConfirmRemoveId(null);
  };

  const getName = (userId: string) => {
    const u = assigneeCandidates.find((c) => c.userId === userId);
    return u?.name ?? u?.email ?? userId;
  };

  const getRecord = (userId: string) => assigneeCandidates.find((c) => c.userId === userId);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-700">
          {assigneeRoleLabel}s of{' '}
          <span className="text-violet-700">{subjectName}</span>
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">
          {assignments.length} {assigneeRoleLabel.toLowerCase()}{assignments.length !== 1 ? 's' : ''} assigned to this {subjectRoleLabel.toLowerCase()}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
        </div>
      ) : (
        <div className="px-4 py-4 space-y-4">
          {assignments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No {assigneeRoleLabel.toLowerCase()}s assigned yet.
            </p>
          ) : (
            <div className="space-y-2">
              {assignments.map((a) => {
                const rec = getRecord(a.assigneeId);
                return (
                  <div
                    key={a.assignmentId}
                    className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {a.assigneeName ?? getName(a.assigneeId)}
                      </p>
                      {rec?.email && (
                        <div className="mt-0.5">
                          <UserEmail email={rec.email} role={rec.role} />
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Assigned {new Date(a.createdAt).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <button
                      onClick={() => setConfirmRemoveId(a.assignmentId)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove assignment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Add {assigneeRoleLabel}
            </p>
            <div className="flex gap-2">
              <select
                value={selectedAssigneeId}
                onChange={(e) => setSelectedAssigneeId(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
              >
                <option value="">— Select {assigneeRoleLabel.toLowerCase()} —</option>
                {available.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name ?? u.email ?? u.userId}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={!selectedAssigneeId || isAdding}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAdding ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmRemoveId}
        message={removeConfirmMessage}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setConfirmRemoveId(null)}
      />
    </div>
  );
}

// ─── Tab: Supervisors of Enumerators (existing supervisor_assignments) ─────────

function SupervisorsOfEnumeratorsTab({
  enumerators,
  supervisors,
  onToast,
}: {
  enumerators: UserRecord[];
  supervisors: UserRecord[];
  onToast: (type: 'success' | 'error', message: string) => void;
}) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['supervisor-assignments', selectedId],
    queryFn: () => annotationApi.listAssignments({ enumeratorId: selectedId! }),
    enabled: !!selectedId,
  });

  const assignments: GenericAssignmentRow[] = rows.map((r) => ({
    assignmentId: r.assignmentId,
    assigneeId: r.supervisorId,
    assigneeName: r.supervisorName,
    createdAt: r.createdAt,
  }));

  const createMutation = useMutation({
    mutationFn: (supervisorId: string) =>
      annotationApi.createAssignment({ enumeratorId: selectedId!, supervisorId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor-assignments', selectedId] });
      onToast('success', 'Supervisor assigned.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => annotationApi.deleteAssignment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor-assignments', selectedId] });
      onToast('success', 'Assignment removed.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });

  const selectedSubject = enumerators.find((e) => e.userId === selectedId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1">
        <SubjectList
          label="Enumerators"
          subjects={enumerators}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </div>
      <div className="lg:col-span-2">
        {!selectedId ? (
          <EmptyState message="Select an enumerator to manage their supervisor assignments." />
        ) : (
          <AssignmentPanel
            subjectName={selectedSubject?.name ?? selectedSubject?.email ?? selectedId}
            subjectRoleLabel="Enumerator"
            assigneeRoleLabel="Supervisor"
            assigneeCandidates={supervisors}
            assignments={assignments}
            isLoading={isLoading}
            onAdd={(supervisorId) => createMutation.mutateAsync(supervisorId)}
            onRemove={(id) => deleteMutation.mutateAsync(id)}
            removeConfirmMessage="Remove this supervisor assignment? The enumerator's tasks will no longer be reviewed by this supervisor."
          />
        )}
      </div>
    </div>
  );
}

// ─── Tab: Transcribers of Enumerators ──────────────────────────────────────────

function TranscribersOfEnumeratorsTab({
  enumerators,
  transcribers,
  onToast,
}: {
  enumerators: UserRecord[];
  transcribers: UserRecord[];
  onToast: (type: 'success' | 'error', message: string) => void;
}) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['transcriber-assignments', 'by-enumerator', selectedId],
    queryFn: () => annotationApi.listTranscriberAssignments({ enumeratorId: selectedId! }),
    enabled: !!selectedId,
  });

  const assignments: GenericAssignmentRow[] = rows.map((r) => ({
    assignmentId: r.assignmentId,
    assigneeId: r.transcriberId,
    createdAt: r.createdAt,
  }));

  const createMutation = useMutation({
    mutationFn: (transcriberId: string) =>
      annotationApi.createTranscriberAssignment({ enumeratorId: selectedId!, transcriberId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transcriber-assignments', 'by-enumerator', selectedId] });
      onToast('success', 'Transcriber assigned.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => annotationApi.deleteTranscriberAssignment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transcriber-assignments', 'by-enumerator', selectedId] });
      onToast('success', 'Assignment removed.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });

  const selectedSubject = enumerators.find((e) => e.userId === selectedId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1">
        <SubjectList
          label="Enumerators"
          subjects={enumerators}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </div>
      <div className="lg:col-span-2">
        {!selectedId ? (
          <EmptyState message="Select an enumerator to manage which transcribers transcribe their uploads." />
        ) : (
          <AssignmentPanel
            subjectName={selectedSubject?.name ?? selectedSubject?.email ?? selectedId}
            subjectRoleLabel="Enumerator"
            assigneeRoleLabel="Transcriber"
            assigneeCandidates={transcribers}
            assignments={assignments}
            isLoading={isLoading}
            onAdd={(transcriberId) => createMutation.mutateAsync(transcriberId)}
            onRemove={(id) => deleteMutation.mutateAsync(id)}
            removeConfirmMessage="Remove this transcriber assignment? They will no longer have access to this enumerator's uploads (existing edits are preserved)."
          />
        )}
      </div>
    </div>
  );
}

// ─── Tab: Supervisors of Transcribers ──────────────────────────────────────────

function SupervisorsOfTranscribersTab({
  transcribers,
  supervisors,
  onToast,
}: {
  transcribers: UserRecord[];
  supervisors: UserRecord[];
  onToast: (type: 'success' | 'error', message: string) => void;
}) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['transcriber-supervisor-assignments', 'by-transcriber', selectedId],
    queryFn: () => annotationApi.listTranscriberSupervisorAssignments({ transcriberId: selectedId! }),
    enabled: !!selectedId,
  });

  const assignments: GenericAssignmentRow[] = rows.map((r) => ({
    assignmentId: r.assignmentId,
    assigneeId: r.supervisorId,
    createdAt: r.createdAt,
  }));

  const createMutation = useMutation({
    mutationFn: (supervisorId: string) =>
      annotationApi.createTranscriberSupervisorAssignment({ transcriberId: selectedId!, supervisorId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transcriber-supervisor-assignments', 'by-transcriber', selectedId] });
      onToast('success', 'Supervisor assigned to transcriber.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => annotationApi.deleteTranscriberSupervisorAssignment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transcriber-supervisor-assignments', 'by-transcriber', selectedId] });
      onToast('success', 'Assignment removed.');
    },
    onError: (err: Error) => onToast('error', err.message),
  });

  const selectedSubject = transcribers.find((t) => t.userId === selectedId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1">
        <SubjectList
          label="Transcribers"
          subjects={transcribers}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </div>
      <div className="lg:col-span-2">
        {!selectedId ? (
          <EmptyState message="Select a transcriber to assign supervisors who can review their submitted work." />
        ) : (
          <AssignmentPanel
            subjectName={selectedSubject?.name ?? selectedSubject?.email ?? selectedId}
            subjectRoleLabel="Transcriber"
            assigneeRoleLabel="Supervisor"
            assigneeCandidates={supervisors}
            assignments={assignments}
            isLoading={isLoading}
            onAdd={(supervisorId) => createMutation.mutateAsync(supervisorId)}
            onRemove={(id) => deleteMutation.mutateAsync(id)}
            removeConfirmMessage="Remove this supervisor's review access to this transcriber's submissions?"
          />
        )}
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center h-full flex flex-col items-center justify-center gap-3">
      <Users className="w-12 h-12 text-gray-200" />
      <p className="text-gray-500 font-medium">Select a user on the left</p>
      <p className="text-gray-400 text-sm">{message}</p>
    </div>
  );
}

// ─── Bulk Task Creation Section ────────────────────────────────────────────────

function BulkCreateTasksSection({
  onToast,
}: {
  onToast: (type: 'success' | 'error', message: string) => void;
}) {
  const qc = useQueryClient();
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);

  const { data: sessions = [], isLoading } = useQuery<SessionRecord[]>({
    queryKey: ['sessions', 'completed'],
    queryFn: () => getSessions({ status: 'completed' }).then((d) => d.sessions ?? d ?? []),
  });

  const toggleSession = (id: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedSessionIds.size === sessions.length) setSelectedSessionIds(new Set());
    else setSelectedSessionIds(new Set(sessions.map((s) => s.sessionId)));
  };

  const handleCreateTasks = async () => {
    if (selectedSessionIds.size === 0) return;
    setIsCreating(true);
    let successCount = 0;
    let errorCount = 0;
    for (const sessionId of selectedSessionIds) {
      try {
        await annotationApi.createTask({ sessionId });
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setIsCreating(false);
    qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    setSelectedSessionIds(new Set());
    if (errorCount === 0) {
      onToast('success', `${successCount} task${successCount !== 1 ? 's' : ''} created.`);
    } else {
      onToast('error', `${successCount} created, ${errorCount} failed. Some tasks may already exist.`);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">Bulk Create Annotation Tasks</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Select completed sessions to create annotation tasks
          </p>
        </div>
        {selectedSessionIds.size > 0 && (
          <button
            onClick={handleCreateTasks}
            disabled={isCreating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50"
          >
            {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create {selectedSessionIds.size} Task{selectedSessionIds.size !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="px-4 py-8 text-center text-gray-400 text-sm">
          No completed sessions available.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleAll} className="text-gray-500 hover:text-gray-700">
                    {selectedSessionIds.size === sessions.length ? (
                      <CheckSquare className="w-4 h-4 text-violet-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Session ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Enumerator</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.map((s) => (
                <tr
                  key={s.sessionId}
                  onClick={() => toggleSession(s.sessionId)}
                  className={`cursor-pointer transition-colors ${
                    selectedSessionIds.has(s.sessionId) ? 'bg-violet-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3">
                    {selectedSessionIds.has(s.sessionId) ? (
                      <CheckSquare className="w-4 h-4 text-violet-600" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-300" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                      {s.sessionId.slice(0, 12)}…
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {s.enumeratorId ? s.enumeratorId.slice(0, 8) + '…' : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {s.createdAt
                      ? new Date(s.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {s.location?.state
                      ? `${s.location.state}${s.location.district ? ', ' + s.location.district : ''}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

type Tab = 'sup-enum' | 'trans-enum' | 'sup-trans';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'sup-enum',   label: 'Supervisors of Enumerators' },
  { id: 'trans-enum', label: 'Transcribers of Enumerators' },
  { id: 'sup-trans',  label: 'Supervisors of Transcribers' },
];

export default function AssignmentsPage() {
  const { toasts, add: addToast } = useToasts();
  const [activeTab, setActiveTab] = useState<Tab>('sup-enum');

  // Bump the limit past the backend's default 50. The /users approvalStatus
  // index sorts by createdAt DESC, and the role filter runs in-memory after
  // the GSI page is fetched — so without a high limit, recently-approved
  // users displace older approved users out of the first page entirely
  // (not just within the same role), making them invisible on this page.
  const { data: enumerators = [], isLoading: enumLoading, isError: enumError } = useQuery<UserRecord[]>({
    queryKey: ['users', 'enumerator', 'approved'],
    queryFn: () => getUsers({ role: 'enumerator', approvalStatus: 'approved', limit: '5000' }).then((d) => d.users ?? d ?? []),
  });
  const { data: transcribers = [] } = useQuery<UserRecord[]>({
    queryKey: ['users', 'transcriber', 'approved'],
    queryFn: () => getUsers({ role: 'transcriber', approvalStatus: 'approved', limit: '5000' }).then((d) => d.users ?? d ?? []),
  });
  const { data: supervisors = [] } = useQuery<UserRecord[]>({
    queryKey: ['users', 'supervisor', 'approved'],
    queryFn: () => getUsers({ role: 'supervisor', approvalStatus: 'approved', limit: '5000' }).then((d) => d.users ?? d ?? []),
  });

  if (enumLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (enumError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="text-gray-600 font-medium">Failed to load users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium pointer-events-auto ${
              t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
          <LinkIcon className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage who reviews and transcribes whose work
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'sup-enum' && (
        <SupervisorsOfEnumeratorsTab
          enumerators={enumerators}
          supervisors={supervisors}
          onToast={addToast}
        />
      )}
      {activeTab === 'trans-enum' && (
        <TranscribersOfEnumeratorsTab
          enumerators={enumerators}
          transcribers={transcribers}
          onToast={addToast}
        />
      )}
      {activeTab === 'sup-trans' && (
        <SupervisorsOfTranscribersTab
          transcribers={transcribers}
          supervisors={supervisors}
          onToast={addToast}
        />
      )}

      <div className="border-t border-gray-200 pt-6">
        <BulkCreateTasksSection onToast={addToast} />
      </div>
    </div>
  );
}
